import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../db';
import { getApiBaseUrl, getAuthorizationToken, updateCustomCosyflow } from '../api-helper';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ReactComponent as InfoIcon } from '../images/icons/info.svg';
import { useAccountContext } from '../context/AccountContext';
import { MediaRenderer, MediaTypeBadge, detectMediaType } from '../components/CosyflowEditorMedia';
import ModelGalleryPicker from '../components/ModelGalleryPicker';

const ModelDirectories = [
  'audio_encoders',
  'checkpoints',
  'clip',
  'clip_vision',
  'configs',
  'controlnet',
  'custom_nodes',
  'diffusers',
  'diffusion_models',
  'embeddings',
  'facedetection',
  'facerestore_models',
  'gligen',
  'grounding-dino',
  'hypernetworks',
  'insightface',
  'instantid',
  'ipadapter',
  'latent_upscale_models',
  'loras',
  'mmdets',
  'model_patches',
  'photomaker',
  'pulid',
  'reactor',
  'sams',
  'style_models',
  'text_encoders',
  'ultralytics',
  'unet',
  'upscale_models',
  'vae',
  'vae_approx',
];

const CosyflowEditor = () => {
  // State management
  const [cosyflows, setCosyflows] = useState([]);
  const accountContext = useAccountContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [customCosyflows, setCustomCosyflows] = useState([]);
  const [selectedCosyflowId, setSelectedCosyflowId] = useState(null);
  const [editedCosyflow, setEditedCosyflow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [installLocally, setInstallLocally] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exampleImages, setExampleImages] = useState([]);
  const [cosyflowPrefix, setCosyflowPrefix] = useState('');
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [isGalleryPickerOpen, setIsGalleryPickerOpen] = useState(false);
  const [isFromComfyUI, setIsFromComfyUI] = useState(false);
  const [hasEditedName, setHasEditedName] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    exampleMedia: false,
    modelConfig: true,
    fieldConfig: true
  });
  const [urlTestStatus, setUrlTestStatus] = useState({});
  const [directoryDropdownOpen, setDirectoryDropdownOpen] = useState({});
  const [directorySearchQuery, setDirectorySearchQuery] = useState({});
  const [originalImportance, setOriginalImportance] = useState({});

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const testModelUrl = async (variableKey, url) => {
    if (!url) {
      return;
    }

    setUrlTestStatus(prev => ({ ...prev, [variableKey]: 'loading' }));

    try {
      const response = await fetch(url, { method: 'HEAD' });

      const contentLength = response.headers.get('content-length');

      if (contentLength) {
        handleModelVariableChange(variableKey, 'size', contentLength);
      }

      setUrlTestStatus(prev => ({ ...prev, [variableKey]: response.ok ? 'success' : 'error' }));

      setTimeout(() => {
        setUrlTestStatus(prev => ({ ...prev, [variableKey]: null }));
      }, 3000);
    } catch (error) {
      console.error('[CosyflowEditor] URL Test Error:', error);
      setUrlTestStatus(prev => ({ ...prev, [variableKey]: 'error' }));

      setTimeout(() => {
        setUrlTestStatus(prev => ({ ...prev, [variableKey]: null }));
      }, 3000);
    }
  };

  // Load all cosyflows from IndexedDB on component mount (skip if importing)
  useEffect(() => {
    if (!location.state?.importedCosyflow) {
      loadCosyflows();
    }
  }, []);

  // Load custom cosyflows when account details are available
  useEffect(() => {
    if (accountContext.accountDetails?.accountNo) {
      loadCustomCosyflows();
    }
  }, [accountContext.accountDetails?.accountNo]);

  // Handle imported cosyflow from navigation state (e.g., from ComfyUI)
  useEffect(() => {
    if (location.state?.importedCosyflow) {
      const imported = location.state.importedCosyflow;
      console.log('[CosyflowEditor] Received imported cosyflow:', imported);

      try {
        // Parse the cosyflow JSON string if it exists (handle both string and object)
        const parsedCosyflow = imported.cosyflow
          ? (typeof imported.cosyflow === 'string' ? JSON.parse(imported.cosyflow) : imported.cosyflow)
          : {};

        // Build the editor-compatible structure
        const editorCosyflow = {
          uid: imported.uid || `imported-${Date.now()}`,
          account_no: imported.account_no || imported.accountNo,
          trusted: imported.trusted === '1' || imported.trusted === 1,
          public: imported.public === '1' || imported.public === 1,
          distributed: imported.distributed === '1' || imported.distributed === 1 || imported.distributed === true,
          isCustom: false,
          isImported: true,
          data: {
            title: imported.title || parsedCosyflow.title || 'Imported Cosyflow',
            description: imported.description || parsedCosyflow.description || '',
            category: parsedCosyflow.category || imported.category || '',
            variables: parsedCosyflow.variables || {},
            workflow: parsedCosyflow.workflow || {},
            prompt: parsedCosyflow.prompt || {},
            assignments: parsedCosyflow.assignments || [],
            metadata: parsedCosyflow.metadata || {}
          }
        };

        // Extract prefix from title
        const title = editorCosyflow.data.title || '';
        const prefixMatch = title.match(/^\([^)]+\)\s*/);
        setCosyflowPrefix(prefixMatch ? prefixMatch[0] : '');

        // Store original importance values for required fields (to prevent accidental locking)
        const origImportance = {};
        if (editorCosyflow.data?.variables) {
          Object.entries(editorCosyflow.data.variables).forEach(([key, field]) => {
            if (field?.required === true && field?.importance === 5) {
              origImportance[key] = 5;
            }
          });
        }
        setOriginalImportance(origImportance);

        // Set the imported cosyflow as the edited cosyflow
        setEditedCosyflow(editorCosyflow);
        setSelectedCosyflowId(editorCosyflow.uid);
        setFieldSearchQuery('');
        setExampleImages(editorCosyflow.data.metadata?.example_urls || []);

        // Expand the Field Configuration section so user can see the imported fields
        setCollapsedSections(prev => ({
          ...prev,
          fieldConfig: false
        }));

        // Check if this is a new cosyflow from ComfyUI
        setIsFromComfyUI(location.state?.isNewFromComfyUI === true);

        setMessage({
          type: 'success',
          text: `Loaded imported cosyflow: "${editorCosyflow.data.title}"`
        });

        console.log('[CosyflowEditor] Imported cosyflow loaded:', editorCosyflow);
        console.log('[CosyflowEditor] Variables count:', Object.keys(editorCosyflow.data.variables || {}).length);
        console.log('[CosyflowEditor] Variables keys:', Object.keys(editorCosyflow.data.variables || {}));
        console.log('[CosyflowEditor] Workflow nodes count:', (editorCosyflow.data.workflow?.nodes || []).length);
        console.log('[CosyflowEditor] Prompt keys:', Object.keys(editorCosyflow.data.prompt || {}));
      } catch (error) {
        console.error('[CosyflowEditor] Error parsing imported cosyflow:', error);
        setMessage({
          type: 'error',
          text: 'Failed to parse imported cosyflow data'
        });
      }

      // Clear the navigation state to prevent re-loading on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadCosyflows = async () => {
    try {
      setLoading(true);
      const apiInUse = await getApiBaseUrl();
      const res = await axios.get(`${apiInUse}/api/cosyflows/query`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAuthorizationToken()}`
        }
      });
      console.log('[CosyflowEditor] Loaded cosyflows from API:', res.data);

      if (res.data && res.data.cosyflows) {
        const parsedCosyflows = res.data.cosyflows.map((item, index) => {
          const cosyflowData = typeof item.cosyflow === 'string' ? JSON.parse(item.cosyflow) : item.cosyflow;
          return {
            id: item.id || `api-${index}`,
            uid: item.uid,
            account_no: item.account_no,
            dbId: item.id,
            data: cosyflowData,
            timestamp: item.created_at || Date.now(),
            trusted: item.trusted,
            public: item.public
          };
        });
        setCosyflows(parsedCosyflows);
      }
    } catch (error) {
      console.error('[CosyflowEditor] Error loading cosyflows:', error);
      setMessage({ type: 'error', text: 'Failed to load cosyflows' });
    } finally {
      setLoading(false);
    }
  };

  const loadCustomCosyflows = async () => {
    try {
      const apiInUse = await getApiBaseUrl()
      const res = await axios.get(`${apiInUse}/api/cosyflows/query?account_no=${accountContext.accountDetails.accountNo}`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAuthorizationToken()}`
        }
      });
      console.log('[CosyflowEditor] Custom Cosyflows: ', res);

      if (res.data && res.data.cosyflows) {
        const parsedCosyflows = res.data.cosyflows.map((item, index) => {
          const cosyflowData = typeof item.cosyflow === 'string' ? JSON.parse(item.cosyflow) : item.cosyflow;
          return {
            id: item.id || `custom-${index}`,
            uid: item.uid,
            account_no: item.account_no,
            dbId: item.id,
            data: cosyflowData,
            timestamp: item.created_at || Date.now(),
            isCustom: true,
            trusted: item.trusted,
            public: item.public
          };
        });
        setCustomCosyflows(parsedCosyflows);
        console.log('[CosyflowEditor] Parsed custom cosyflows:', parsedCosyflows);
      }
    } catch (error) {
      console.error('[CosyflowEditor] Error loading custom cosyflows:', error);
      setMessage({ type: 'error', text: 'Failed to load custom cosyflows' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cosyflow selection
  const handleSelectCosyflow = (cosyflowId) => {
    const selected = cosyflows.find(c => c.id === cosyflowId);
    if (selected) {
      // Deep clone to avoid mutating original data
      const cloned = JSON.parse(JSON.stringify(selected));
      setEditedCosyflow(cloned);
      setSelectedCosyflowId(cosyflowId);
      setMessage(null);

      // Extract prefix from title
      const title = cloned.data?.title || '';
      const prefixMatch = title.match(/^\([^)]+\)\s*/);
      setCosyflowPrefix(prefixMatch ? prefixMatch[0] : '');

      // Reset field search
      setFieldSearchQuery('');

      // Load existing example images if any
      const existingImages = cloned.data?.metadata?.example_urls || [];
      setExampleImages(existingImages);

      console.log('[CosyflowEditor] Selected cosyflow:', cloned.data.title);
    }
  };

  // Handle custom cosyflow selection
  const handleSelectCustomCosyflow = (cosyflowId) => {
    const selected = customCosyflows.find(c => c.id === cosyflowId);
    if (selected) {
      // Deep clone to avoid mutating original data
      const cloned = JSON.parse(JSON.stringify(selected));
      setEditedCosyflow(cloned);
      setSelectedCosyflowId(cosyflowId);
      setMessage(null);

      // Extract prefix from title
      const title = cloned.data?.title || '';
      const prefixMatch = title.match(/^\([^)]+\)\s*/);
      setCosyflowPrefix(prefixMatch ? prefixMatch[0] : '');

      // Reset field search
      setFieldSearchQuery('');

      // Load existing example images if any
      const existingImages = cloned.data?.metadata?.example_urls || [];
      setExampleImages(existingImages);

      console.log('[CosyflowEditor] Selected custom cosyflow:', cloned);
    }
  };

  // Handle name change
  const handleNameChange = (newName) => {
    if (editedCosyflow) {
      setHasEditedName(true);
      setEditedCosyflow({
        ...editedCosyflow,
        data: {
          ...editedCosyflow.data,
          title: cosyflowPrefix + newName
        }
      });
    }
  };

  const getNameWithoutPrefix = () => {
    const title = editedCosyflow?.data?.title || '';
    if (isFromComfyUI && !hasEditedName) {
      return "";
    }
    if (title.includes('no') && title.includes('title')) {
      return ""
    }
    return cosyflowPrefix ? title.replace(cosyflowPrefix, '') : title;
  };

  const getCategory = () => {
    const currentCategory = editedCosyflow?.data?.category || '';
    const lastSegment = currentCategory.split('/').pop()?.trim() || '';
    const validOptions = ['Image', 'Video', 'Audio', '3D'];
    const matched = validOptions.find(opt => opt.toLowerCase() === lastSegment.toLowerCase());
    return matched || ''
  }

  // Handle category change
  const handleCategoryChange = (newCategory) => {
    if (editedCosyflow) {
      setEditedCosyflow({
        ...editedCosyflow,
        data: {
          ...editedCosyflow.data,
          category: newCategory
        }
      });
    }
  };

  // Handle field importance change
  const handleImportanceChange = (fieldKey, newImportance) => {
    if (!editedCosyflow) return;

    const field = editedCosyflow.data.variables?.[fieldKey];

    // Prevent changing importance of truly required fields (only those originally at importance 5)
    if (field?.required === true && originalImportance[fieldKey] === 5) {
      setMessage({
        type: 'warning',
        text: `Cannot modify importance of required field: ${field.title || fieldKey}`
      });
      return;
    }

    setEditedCosyflow({
      ...editedCosyflow,
      data: {
        ...editedCosyflow.data,
        variables: {
          ...editedCosyflow.data.variables,
          [fieldKey]: {
            ...editedCosyflow.data.variables[fieldKey],
            importance: parseInt(newImportance)
          }
        }
      }
    });

    setMessage(null);
  };

  // Handle default value change
  const handleDefaultChange = (fieldKey, newDefault) => {
    if (!editedCosyflow) return;

    setEditedCosyflow({
      ...editedCosyflow,
      data: {
        ...editedCosyflow.data,
        variables: {
          ...editedCosyflow.data.variables,
          [fieldKey]: {
            ...editedCosyflow.data.variables[fieldKey],
            default: newDefault
          }
        }
      }
    });
  };

  // Handle field title change
  const handleFieldTitleChange = (fieldKey, newTitle) => {
    if (!editedCosyflow) return;

    setEditedCosyflow({
      ...editedCosyflow,
      data: {
        ...editedCosyflow.data,
        variables: {
          ...editedCosyflow.data.variables,
          [fieldKey]: {
            ...editedCosyflow.data.variables[fieldKey],
            title: newTitle
          }
        }
      }
    });
  };

  const handleGallerySelect = (imgObj) => {
    const mediaUrl = typeof imgObj === 'string' ? imgObj : (imgObj?.url || imgObj?.image);
    if (!mediaUrl) return;
    if (!exampleImages.includes(mediaUrl)) {
      setExampleImages(prev => [...prev, mediaUrl]);
      setMessage({
        type: 'success',
        text: 'Media added successfully!'
      });
    } else {
      setMessage({
        type: 'info',
        text: 'Media already added'
      });
    }
  };

  // Handle image removal
  const handleRemoveImage = (index) => {
    setExampleImages(prev => prev.filter((_, i) => i !== index));
    setMessage({
      type: 'success',
      text: 'Image removed successfully!'
    });
  };

  // const validateModelUrls = () => {
  //   const modelVariables = getModelVariables();
  //   const invalidModels = [];

  //   for (const modelVar of modelVariables) {
  //     if (modelVar.modelInfo?.url) {
  //       const nodeId = modelVar.nodeData?.id;
  //       const currentUrl = modelVar.modelInfo.url;
  //       const validatedUrl = validatedUrls[nodeId];

  //       if (!validatedUrl) {
  //         invalidModels.push({
  //           name: modelVar.title || modelVar.key,
  //           reason: 'URL has not been tested'
  //         });
  //       } else if (validatedUrl !== currentUrl) {
  //         invalidModels.push({
  //           name: modelVar.title || modelVar.key,
  //           reason: 'URL was changed after validation - please re-test'
  //         });
  //       }
  //     }
  //   }

  //   return invalidModels;
  // };

  // Submit new cosyflow to API
  const handleSubmit = async () => {
    if (!editedCosyflow) {
      setMessage({ type: 'error', text: 'No cosyflow selected' });
      return;
    }

    const titleLower = (editedCosyflow.data.title || '').toLowerCase();
    if (!editedCosyflow.data.title || editedCosyflow.data.title.trim() === '' ||
        (titleLower.includes('no') && titleLower.includes('title'))) {
      setMessage({ type: 'error', text: 'Please enter a valid cosyflow name' });
      return;
    }

    if (!editedCosyflow.data.category || editedCosyflow.data.category.trim() === '') {
      setMessage({ type: 'error', text: 'Please select a category' });
      return;
    }

    if (!editedCosyflow.data.description || editedCosyflow.data.description.trim() === '') {
      setMessage({ type: 'error', text: 'Please enter a description' });
      return;
    }

    const variables = editedCosyflow.data.variables || {};
    const hasImportance5Field = Object.values(variables).some(field => field?.importance === 5);
    if (!hasImportance5Field) {
      setMessage({
        type: 'error',
        text: 'Cosyflow must have at least one required field (importance level 5). Please set at least one field to importance 5.'
      });
      return;
    }

    // const invalidModels = validateModelUrls();
    // if (invalidModels.length > 0) {
    //   const errorDetails = invalidModels.map(m => `• ${m.name}: ${m.reason}`).join('\n');
    //   setMessage({
    //     type: 'error',
    //     text: `Model URL validation failed:\n${errorDetails}\n\nPlease test all model URLs before saving.`
    //   });
    //   console.error('[CosyflowEditor] Model URL validation failed:', invalidModels);
    //   return;
    // }

    try {
      setSaving(true);
      setMessage({ type: 'info', text: 'Saving new cosyflow...' });

      const apiBase = await getApiBaseUrl();

      // Update metadata with example images
      const updatedMetadata = {
        ...editedCosyflow.data.metadata,
        example_urls: exampleImages
      };

      // Update the cosyflow data with new metadata
      editedCosyflow.data.metadata = updatedMetadata;

      // Replace any existing prefix with (cosy) alias: for new cosyflows
      const nameWithoutPrefix = editedCosyflow.data.title.replace(/^\([^)]+\)\s*/, '').replace(/^[^:]+:\s*/, '');
      const alias = accountContext.accountDetails?.alias || 'Unknown';
      const newTitle = `(cosy) ${alias}: ${nameWithoutPrefix}`;
      editedCosyflow.data.title = newTitle;

      // Prepare the payload
      const payload = {
        title: newTitle,
        cosyflow: JSON.stringify(editedCosyflow.data),
        category: editedCosyflow.data.category || 'Custom',
        description: editedCosyflow.data.description || '',
        metadata: updatedMetadata,
        uid: editedCosyflow.uid,
        account_no: accountContext.accountDetails.accountNo,
        public: editedCosyflow.public == 1 || editedCosyflow.public === true ? 1 : 0,
        trusted:  editedCosyflow.trusted == 1 || editedCosyflow.trusted === true ? 1 : 0,
        distributed: editedCosyflow.distributed == 1 || editedCosyflow.distributed === true ? 1 : 0
      };

      console.log('[CosyflowEditor] Submitting new cosyflow:', payload);

      // POST to API endpoint
      const response = await axios.post(
        `${apiBase}/api/cosyflows/create`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthorizationToken()}`
          }
        }
      );

      console.log('[CosyflowEditor] Response:', response.data);

      // Check for existing cosyflow with same title to prevent duplicates
      const existingCosyflow = await db.cosyflows
        .where('data.title')
        .equals(editedCosyflow.data.title)
        .first();

      if (existingCosyflow) {
        // Update existing record instead of creating duplicate
        await db.cosyflows.update(existingCosyflow.id, {
          data: editedCosyflow.data,
          timestamp: Date.now()
        });
        console.log('[CosyflowEditor] Updated existing cosyflow with ID:', existingCosyflow.id);
      } else {
        // Create new record only if title doesn't exist
        const newId = await db.cosyflows.add({
          data: editedCosyflow.data,
          timestamp: Date.now()
        });
        console.log('[CosyflowEditor] Created new cosyflow with ID:', newId);
      }

      if (installLocally) {
        installCosyflowLocally(editedCosyflow.data).catch(installError => {
          console.error('[CosyflowEditor] Error installing locally:', installError);
        });
      }
      setMessage({
        type: 'success',
        text: installLocally
          ? `Successfully saved cosyflow. Installing locally in background...`
          : existingCosyflow
            ? `Successfully updated cosyflow: "${editedCosyflow.data.title}"`
            : `Successfully created new cosyflow: "${editedCosyflow.data.title}"`
      });

      setTimeout(() => {
        navigate('/cosyflows', { state: { showOnlyMine: true } });
      }, 1500);
      // Reload cosyflows and reset selection
      // await loadCosyflows();
      // setEditedCosyflow(null);
      // setSelectedCosyflowId(null);
      // setExampleImages([]);
    } catch (error) {
      console.error('[CosyflowEditor] Error saving cosyflow:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save cosyflow';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const installCosyflowLocally = async (cosyflowData) => {
    const localCosyServer = await db.settings.get({ key: 'local_cosy' });
    if (!localCosyServer?.value) {
      throw new Error('Local Cosyflow server not found. Please ensure your local server is running.');
    }

    const response = await axios.post(
      `${localCosyServer.value}/api/cosy/install-local`,
      { cosyflow: cosyflowData },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('[CosyflowEditor] Install local response:', response.data);
    return response.data;
  };

  const handleInstallLocallyToggle = async () => {
    if (!installLocally) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);

        const response = await axios.get('http://localhost:7412/status', {
          signal: controller.signal,
          timeout: 2000
        });
        clearTimeout(timeoutId);

        const statuses = Array.isArray(response.data) ? response.data : [];
        const cosyServer = statuses.find(s => s.name === 'cosy' && s.status === 'ready');

        if (!cosyServer) {
          Swal.fire({
            title: 'Server Not Available',
            text: 'Local Cosyflow server not detected or not ready. Please ensure your local server is running.',
            icon: 'error',
            confirmButtonColor: '#FFB02E',
          });
          return;
        }
      } catch (error) {
        Swal.fire({
          title: 'Server Not Available',
          text: 'Could not connect to server manager. Please ensure Promptus Manager is running.',
          icon: 'error',
          confirmButtonColor: '#FFB02E',
        });
        return;
      }
    }
    setInstallLocally(!installLocally);
  };

  const handleUpdate = async () => {
    const titleLower = (editedCosyflow.data.title || '').toLowerCase();
    if (!editedCosyflow.data.title || editedCosyflow.data.title.trim() === '' ||
        (titleLower.includes('no') && titleLower.includes('title'))) {
      setMessage({ type: 'error', text: 'Please enter a valid cosyflow name' });
      return;
    }

    if (!editedCosyflow.data.category || editedCosyflow.data.category.trim() === '') {
      setMessage({ type: 'error', text: 'Please select a category' });
      return;
    }

    if (!editedCosyflow.data.description || editedCosyflow.data.description.trim() === '') {
      setMessage({ type: 'error', text: 'Please enter a description' });
      return;
    }

    // const invalidModels = validateModelUrls();
    // if (invalidModels.length > 0) {
    //   const errorDetails = invalidModels.map(m => `• ${m.name}: ${m.reason}`).join('\n');
    //   setMessage({
    //     type: 'error',
    //     text: `Model URL validation failed:\n${errorDetails}\n\nPlease test all model URLs before updating.`
    //   });
    //   console.error('[CosyflowEditor] Model URL validation failed:', invalidModels);
    //   return;
    // }

    try {
      setSaving(true);
      setMessage({ type: 'info', text: 'Updating cosyflow...' });

      const apiBase = await getApiBaseUrl();
      const authToken = sessionStorage.getItem('api_token') || process.env.REACT_APP_PUBLIC_TOKEN;

      const updatedMetadata = {
        ...editedCosyflow.data.metadata,
        example_urls: exampleImages
      };

      editedCosyflow.data.metadata = updatedMetadata;

      const nameWithoutPrefix = editedCosyflow.data.title.replace(/^\([^)]+\)\s*/, '').replace(/^[^:]+:\s*/, '');
      const alias = accountContext.accountDetails?.alias || 'Unknown';
      const updatedTitle = `(cosy) ${alias}: ${nameWithoutPrefix}`;
      editedCosyflow.data.title = updatedTitle;

      const payload = {
        title: updatedTitle,
        cosyflow: JSON.stringify(editedCosyflow.data),
        category: editedCosyflow.data.category || 'Custom',
        description: editedCosyflow.data.description || '',
        metadata: updatedMetadata,
        uid: editedCosyflow.uid,
        account_no: editedCosyflow.account_no,
        public: editedCosyflow.public == 1 || editedCosyflow.public === true ? 1 : 0,
        distributed: editedCosyflow.distributed == 1 || editedCosyflow.distributed === true ? 1 : 0
      };

      console.log('[CosyflowEditor] Updating cosyflow:', editedCosyflow);
      console.log('[CosyflowEditor] Payload:', payload);

      const updatedCosylowRes = await updateCustomCosyflow(payload)

      console.log('[CosyflowEditor] Res:', updatedCosylowRes);

      if (installLocally) {
        installCosyflowLocally(editedCosyflow.data).catch(installError => {
          console.error('[CosyflowEditor] Error installing locally:', installError);
        });
      }

      setTimeout(() => {
        navigate('/cosyflows', { state: { showOnlyMine: true } });
      }, 1500);

      // await loadCustomCosyflows();
      // setEditedCosyflow(null);
      // setSelectedCosyflowId(null);
      // setExampleImages([]);
    } catch (error) {
      console.error('[CosyflowEditor] Error updating cosyflow:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to update cosyflow';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  // Filter cosyflows by search query
  const filteredCosyflows = cosyflows.filter(c =>
    c.data?.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCustomCosyflows = customCosyflows.filter(c =>
    c.data?.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getModelVariables = () => {
    if (!editedCosyflow?.data?.variables) {
      return [];
    }

    const modelVars = Object.entries(editedCosyflow.data.variables)
      .filter(([key, value]) => value.type === 'model')
      .map(([key, value]) => ({
        key,
        title: value.title || key,
        default: value.default || '',
        directory: value.directory || '',
        url: value.url || '',
        size: value.size || '',
        tooltip: value.tooltip || '',
        importance: value.importance || 3,
        ...value,
      }));

    return modelVars;
  };

  const handleModelOptionChange = (fieldKey, newValue) => {
    if (!editedCosyflow) return;
    setEditedCosyflow({
      ...editedCosyflow,
      data: {
        ...editedCosyflow.data,
        variables: {
          ...editedCosyflow.data.variables,
          [fieldKey]: {
            ...editedCosyflow.data.variables[fieldKey],
            default: newValue
          }
        }
      }
    });
  };

  const handleModelVariableChange = (variableKey, property, newValue) => {
    if (!editedCosyflow) return;

    setEditedCosyflow({
      ...editedCosyflow,
      data: {
        ...editedCosyflow.data,
        variables: {
          ...editedCosyflow.data.variables,
          [variableKey]: {
            ...editedCosyflow.data.variables[variableKey],
            [property]: newValue,
          }
        }
      }
    });
  };

  const renderModelInputsSection = () => {
    const modelVariables = getModelVariables();
    if (modelVariables.length === 0) return null;

    return (
      <div className="mb-8 pb-6 border-b-2 border-white/30">
        <div
          className="mb-6 cursor-pointer flex items-center justify-between"
          onClick={() => toggleSection('modelConfig')}
        >
          <div>
            <h3 className="text-2xl font-bold text-white mb-1">Model Configuration</h3>
            <p className="text-white/80 text-base">
              Configure the AI models used in this cosyflow
            </p>
          </div>
          <svg
            className={`w-8 h-8 text-cyan transition-transform ${collapsedSections['modelConfig'] ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {!collapsedSections['modelConfig'] && (
        <div className="space-y-4">
          {modelVariables.map((modelVar) => (
            <div
              key={modelVar.key}
              className="bg-dark-purple/30 p-5 rounded-xl border-2 border-white/20 hover:border-cyan/50 transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-white font-bold text-lg">{modelVar.title}</h4>
                  <p className="text-white/60 text-sm">Key: {modelVar.key}</p>
                </div>
                <span className="bg-cyan/20 text-cyan px-3 py-1 rounded-lg text-sm font-bold border border-cyan/50">
                  Model
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-yellow font-bold text-sm mb-2">Name</label>
                  <input
                    type="text"
                    value={modelVar.default || ''}
                    onChange={(e) => handleModelVariableChange(modelVar.key, 'default', e.target.value)}
                    placeholder="Model filename..."
                    className="w-full p-3 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/40 focus:border-cyan focus:outline-none"
                  />
                </div>
                <div className="relative">
                  <label className="block text-yellow font-bold text-sm mb-2">Directory</label>
                  <div
                    className="w-full p-3 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base cursor-pointer flex items-center justify-between hover:border-cyan/50 transition-all"
                    onClick={() => setDirectoryDropdownOpen(prev => ({ ...prev, [modelVar.key]: !prev[modelVar.key] }))}
                  >
                    <span className={modelVar.directory ? 'text-white' : 'text-white/40'}>
                      {modelVar.directory || 'Select directory...'}
                      {modelVar.directory && !ModelDirectories.includes(modelVar.directory) && (
                        <span className="ml-2 text-xs text-yellow/70">(custom)</span>
                      )}
                    </span>
                    <svg className={`w-5 h-5 text-cyan transition-transform ${directoryDropdownOpen[modelVar.key] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {directoryDropdownOpen[modelVar.key] && (
                    <div className="absolute z-50 w-full mt-1 bg-dark-purple border-2 border-cyan/50 rounded-lg shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-white/20">
                        <input
                          type="text"
                          placeholder="Search directories..."
                          value={directorySearchQuery[modelVar.key] || ''}
                          onChange={(e) => setDirectorySearchQuery(prev => ({ ...prev, [modelVar.key]: e.target.value }))}
                          className="w-full p-2 rounded bg-dark-blue/60 border border-white/20 text-white text-sm placeholder-white/40 focus:outline-none focus:border-cyan"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="overflow-y-auto max-h-44">
                        {modelVar.directory && !ModelDirectories.includes(modelVar.directory) && (
                          <div
                            className="px-3 py-2 text-white hover:bg-cyan/20 cursor-pointer flex items-center justify-between border-b border-white/10"
                            onClick={() => {
                              handleModelVariableChange(modelVar.key, 'directory', modelVar.directory);
                              setDirectoryDropdownOpen(prev => ({ ...prev, [modelVar.key]: false }));
                              setDirectorySearchQuery(prev => ({ ...prev, [modelVar.key]: '' }));
                            }}
                          >
                            <span>{modelVar.directory}</span>
                            <span className="text-xs text-yellow/70 bg-yellow/20 px-2 py-0.5 rounded">current (custom)</span>
                          </div>
                        )}
                        {ModelDirectories
                          .filter(dir => !directorySearchQuery[modelVar.key] || dir.toLowerCase().includes(directorySearchQuery[modelVar.key].toLowerCase()))
                          .map(dir => (
                            <div
                              key={dir}
                              className={`px-3 py-2 text-white hover:bg-cyan/20 cursor-pointer ${modelVar.directory === dir ? 'bg-cyan/30' : ''}`}
                              onClick={() => {
                                handleModelVariableChange(modelVar.key, 'directory', dir);
                                setDirectoryDropdownOpen(prev => ({ ...prev, [modelVar.key]: false }));
                                setDirectorySearchQuery(prev => ({ ...prev, [modelVar.key]: '' }));
                              }}
                            >
                              {dir}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-yellow font-bold text-sm mb-2">URL</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={modelVar.url || ''}
                      onChange={(e) => handleModelVariableChange(modelVar.key, 'url', e.target.value)}
                      placeholder="Download URL..."
                      className="flex-1 p-3 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/40 focus:border-cyan focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => testModelUrl(modelVar.key, modelVar.url)}
                      disabled={urlTestStatus[modelVar.key] === 'loading'}
                      className={`px-4 py-3 rounded-lg font-bold text-sm transition-all ${
                        urlTestStatus[modelVar.key] === 'loading'
                          ? 'bg-white/20 text-white/50 cursor-wait'
                          : urlTestStatus[modelVar.key] === 'success'
                          ? 'bg-green-500/80 text-white'
                          : urlTestStatus[modelVar.key] === 'error'
                          ? 'bg-red-500/80 text-white'
                          : 'bg-cyan/30 hover:bg-cyan/50 text-white border border-cyan/50'
                      }`}
                    >
                      {urlTestStatus[modelVar.key] === 'loading' ? 'Testing...' :
                       urlTestStatus[modelVar.key] === 'success' ? '✓ Valid' :
                       urlTestStatus[modelVar.key] === 'error' ? '✗ Failed' :
                       'Test URL'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-yellow font-bold text-sm mb-2">Size (bytes)</label>
                  <input
                    type="text"
                    value={modelVar.size || ''}
                    onChange={(e) => handleModelVariableChange(modelVar.key, 'size', e.target.value)}
                    placeholder="File size in bytes..."
                    className="w-full p-3 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/40 focus:border-cyan focus:outline-none"
                  />
                </div>
              </div>

              {modelVar.tooltip && (
                <p className="text-white/50 text-sm mt-3 italic">
                  {modelVar.tooltip}
                </p>
              )}
            </div>
          ))}
        </div>
        )}
      </div>
    );
  };

  // Render field editor
  const renderFieldEditor = (fieldKey, fieldData) => {
    const isRequired = fieldData.required === true && originalImportance[fieldKey] === 5;
    const currentImportance = fieldData.importance || 4;

    return (
      <div
        key={fieldKey}
        className="bg-dark-blue/40 p-5 rounded-xl border-2 border-white/30 mb-5 hover:border-cyan/50 transition-all"
      >
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <input
              type="text"
              value={fieldData.title || fieldKey}
              onChange={(e) => handleFieldTitleChange(fieldKey, e.target.value)}
              className="text-white font-bold text-xl bg-transparent border-b-2 border-transparent hover:border-white/30 focus:border-cyan focus:outline-none px-1 py-0.5 transition-all flex-1 min-w-0"
              placeholder={fieldKey}
            />
            {isRequired && (
              <span className="bg-red-500/30 text-red-300 px-3 py-1 rounded-lg text-xs font-bold border-2 border-red-500/50 whitespace-nowrap">
                🔒 REQUIRED - LOCKED
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <p className="text-white/70 text-base">
              Type: <span className="text-cyan font-mono font-bold text-lg">{fieldData.type}</span>
            </p>
            <p className="text-white/50 text-sm">
              Key: <span className="text-white/70 font-mono">{fieldKey}</span>
            </p>
            {fieldData.description && (
              <p className="text-white/80 text-sm italic">{fieldData.description}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Importance Slider - Full Width, Bigger */}
          <div className="bg-dark-purple/30 p-4 rounded-xl border border-white/20">
            <label className="block mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-yellow font-bold text-lg">Field Visibility</span>
                <div className="flex items-center gap-3">
                  <span className="text-white bg-yellow/30 px-4 py-2 rounded-lg font-bold text-lg border-2 border-yellow/50">
                    {currentImportance}
                  </span>
                  {isRequired && (
                    <span className="text-red-400 text-sm font-semibold">(Cannot Modify)</span>
                  )}
                </div>
              </div>
            </label>
            <input
              type="range"
              min="1"
              max="5"
              step="1"
              value={currentImportance}
              onChange={(e) => handleImportanceChange(fieldKey, e.target.value)}
              disabled={isRequired}
              className={`w-full h-3 ${isRequired ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                accentColor: '#00D9FF'
              }}
            />
            <div className="flex justify-between mt-3 px-1">
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">1</div>
                <div className="text-white/60 text-sm">Hidden</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">2</div>
                <div className="text-white/60 text-sm">Low</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">3</div>
                <div className="text-white/60 text-sm">Optional</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">4</div>
                <div className="text-white/60 text-sm">High</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 font-bold text-base mb-1">5</div>
                <div className="text-white/60 text-sm">Always</div>
              </div>
            </div>
          </div>

          {/* Default Value - Full Width, Bigger */}
          <div className="bg-dark-purple/30 p-4 rounded-xl border border-white/20">
            <label className="block text-yellow font-bold text-base mb-3">
              Default Value
            </label>
            {fieldData.type === 'BOOLEAN' || fieldData.type === 'boolean' || fieldData.type === 'BOOL' ? (
              <select
                value={fieldData.default === true || fieldData.default === 'true' ? 'true' : 'false'}
                onChange={(e) => handleDefaultChange(fieldKey, e.target.value === 'true')}
                className="w-full p-3 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base font-semibold focus:border-cyan focus:outline-none"
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : fieldData.type === 'enum' || fieldData.type === 'COMBO' || fieldData.type === 'COMFY_DYNAMICCOMBO_V3' ? (
              <select
                value={fieldData.default || ''}
                onChange={(e) => handleDefaultChange(fieldKey, e.target.value)}
                className="w-full p-3 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base font-semibold focus:border-cyan focus:outline-none"
              >
                {fieldData.options?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : fieldData.type === 'FLOAT' || fieldData.type === 'INT' || fieldData.type === 'number' ? (
              <input
                type="number"
                value={fieldData.default ?? ''}
                onChange={(e) => handleDefaultChange(fieldKey, parseFloat(e.target.value))}
                min={fieldData.min}
                max={fieldData.max}
                step={fieldData.step || (fieldData.type === 'FLOAT' ? 0.1 : 1)}
                className="w-full p-3 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base font-semibold focus:border-cyan focus:outline-none"
              />
            ) : (
              <textarea
                value={fieldData.default ?? ''}
                onChange={(e) => handleDefaultChange(fieldKey, e.target.value)}
                placeholder="Enter default value..."
                rows="3"
                className="w-full p-4 rounded-lg bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/40 focus:border-cyan focus:outline-none resize-y"
              />
            )}
            {fieldData.min !== undefined && fieldData.max !== undefined && (
              <p className="text-sm text-white/70 mt-3 font-medium">
                📊 Range: {fieldData.min} to {fieldData.max}
                {fieldData.step && ` • Step: ${fieldData.step}`}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-8">
      <div className="w-full">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-white mb-4">🎨 CosyFlow Editor</h1>
          <p className="text-white/80 text-lg font-medium">
            Clone and customize existing cosyflows by adjusting field visibility and defaults
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mb-8 p-6 rounded-xl border-2 text-base font-semibold ${
              message.type === 'error' ? 'bg-red-500/20 border-red-500/50 text-red-200' :
              message.type === 'success' ? 'bg-green-500/20 border-green-500/50 text-green-200' :
              message.type === 'warning' ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-200' :
              'bg-cyan/20 border-cyan/50 text-cyan'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
          {/* Selector Panels Container */}

          {/* Editor Panel */}
          <div className="lg:col-span-4">
            {!editedCosyflow ? (
              <div className="bg-dark-blue/40 rounded-2xl p-20 border-2 border-white/30 text-center">
                <div className="mb-8">
                  <svg className="w-32 h-32 mx-auto text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">No Cosyflow Selected</h3>
                <p className="text-white/70 text-lg">
                  👈 Select a cosyflow from the list to start customizing
                </p>
              </div>
            ) : (
              <div className="bg-dark-blue/40 rounded-2xl p-6 border-2 border-white/30">
                {/* Example Media Section */}
                <div className="mb-8 pb-6 border-b-2 border-white/30">
                  <div
                    className="mb-6 cursor-pointer flex items-center justify-between"
                    onClick={() => toggleSection('exampleMedia')}
                  >
                    <div>
                      <label className="block text-yellow font-bold text-xl mb-1 cursor-pointer">
                        🖼️ Example Media
                      </label>
                      <p className="text-white/60 text-base font-medium">
                        Select preview media to showcase your cosyflow in the gallery
                      </p>
                    </div>
                    <svg
                      className={`w-8 h-8 text-cyan transition-transform ${collapsedSections['exampleMedia'] ? '' : 'rotate-180'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {!collapsedSections['exampleMedia'] && (
                  <>
                  {/* Select from Gallery Button */}
                  <div className="mb-6">
                    <button
                      onClick={() => setIsGalleryPickerOpen(true)}
                      className="bg-cyan/20 hover:bg-cyan/30 border-2 border-cyan/50 hover:border-cyan text-white font-bold py-4 px-6 rounded-xl transition-all inline-flex items-center gap-2 text-base"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Select from Gallery
                    </button>
                    <p className="text-white/50 text-sm mt-3">
                      Select images generated with this model from your gallery
                    </p>
                  </div>

                  {/* Media Preview Grid */}
                  {exampleImages.length > 0 && (
                    <div>
                      <h4 className="text-white font-bold text-lg mb-4">
                        Selected Media ({exampleImages.length})
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {exampleImages.map((media, index) => {
                          const mediaType = detectMediaType(media);
                          return (
                            <div
                              key={index}
                              className="relative group bg-dark-purple/40 rounded-xl overflow-hidden border-2 border-white/20 hover:border-cyan/50 transition-all"
                            >
                              <div className="w-full h-48">
                                <MediaRenderer
                                  url={media}
                                  alt={`Example ${index + 1}`}
                                  className="w-full h-full"
                                  mediaType={mediaType}
                                />
                              </div>
                              {mediaType !== 'image' && (
                                <div className="absolute top-2 left-2">
                                  <MediaTypeBadge type={mediaType} />
                                </div>
                              )}
                              {/* Remove Button */}
                              <button
                                onClick={() => handleRemoveImage(index)}
                                className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-600 text-white rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-all"
                                title="Remove media"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                              {/* Media Number Badge */}
                              <div className="absolute bottom-2 left-2 bg-black/70 text-white px-3 py-1 rounded-lg text-xs font-bold">
                                #{index + 1}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {exampleImages.length === 0 && (
                    <div className="bg-dark-purple/30 rounded-xl border-2 border-dashed border-white/20 p-10 text-center">
                      <svg className="w-16 h-16 mx-auto text-white/30 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-white/60 text-base font-medium">
                        No example media selected yet
                      </p>
                      <p className="text-white/40 text-sm mt-2">
                        Click "Select from Gallery" above to add preview media
                      </p>
                    </div>
                  )}
                  </>
                  )}
                </div>

                {/* Model Inputs Section */}
                {renderModelInputsSection()}

                {/* Fields Editor */}
                <div className="mb-8">
                  <div
                    className="mb-6 cursor-pointer flex items-center justify-between"
                    onClick={() => toggleSection('fieldConfig')}
                  >
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">⚙️ Field Configuration</h3>
                      <p className="text-white/80 text-base">
                        Customize field visibility and default values. 🔒 Required fields cannot be modified.
                      </p>
                    </div>
                    <svg
                      className={`w-8 h-8 text-cyan transition-transform ${collapsedSections['fieldConfig'] ? '' : 'rotate-180'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {!collapsedSections['fieldConfig'] && (
                  <>
                  {editedCosyflow.data.variables && Object.keys(editedCosyflow.data.variables).length > 0 ? (
                    <>
                      <div className="mb-6">
                        <input
                          type="text"
                          value={fieldSearchQuery}
                          onChange={(e) => setFieldSearchQuery(e.target.value)}
                          placeholder="🔍 Search fields..."
                          className="w-full p-4 rounded-xl bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/50 focus:border-cyan focus:outline-none"
                        />
                        <p className="text-white/50 text-sm mt-2">
                          Showing {Object.entries(editedCosyflow.data.variables).filter(([key, value]) => {
                            if (!fieldSearchQuery) return true;
                            const query = fieldSearchQuery.toLowerCase();
                            return key.toLowerCase().includes(query) ||
                              (value.title && value.title.toLowerCase().includes(query)) ||
                              (value.type && value.type.toLowerCase().includes(query));
                          }).length} of {Object.keys(editedCosyflow.data.variables).length} fields
                        </p>
                      </div>
                      <div className="space-y-6">
                        {Object.entries(editedCosyflow.data.variables)
                          .filter(([key, value]) => {
                            if (!fieldSearchQuery) return true;
                            const query = fieldSearchQuery.toLowerCase();
                            return key.toLowerCase().includes(query) ||
                              (value.title && value.title.toLowerCase().includes(query)) ||
                              (value.type && value.type.toLowerCase().includes(query));
                          })
                          .map(([key, value]) => renderFieldEditor(key, value))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-16 text-white/60 bg-dark-purple/30 rounded-xl border-2 border-white/20">
                      <p className="text-xl font-semibold">No configurable fields found</p>
                    </div>
                  )}
                  </>
                  )}
                </div>

              </div>
            )}
          </div>

          {/* Right Panel - Properties */}
          <div className="lg:col-span-2">
            <div className="bg-dark-blue/40 rounded-2xl p-6 border-2 border-white/30 sticky top-6">
              <h2 className="text-2xl font-bold text-white mb-6">Properties</h2>

              {editedCosyflow ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-yellow font-bold text-base mb-3">
                      Cosyflow Name *
                    </label>
                    <div className="flex items-center w-full rounded-xl bg-dark-purple/60 border-2 border-white/30 focus-within:border-cyan overflow-hidden">
                      {cosyflowPrefix && (
                        <span className="px-3 py-4 bg-cyan/20 text-cyan font-bold text-base border-r border-white/30 whitespace-nowrap">
                          {cosyflowPrefix.trim()}
                        </span>
                      )}
                      <input
                        type="text"
                        value={getNameWithoutPrefix()}
                        onChange={(e) => handleNameChange(e.target.value)}
                        placeholder="Enter cosyflow name..."
                        className="flex-1 p-4 bg-transparent text-white text-base placeholder-white/40 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-yellow font-bold text-base mb-3">
                      Category
                    </label>
                    <select
                      value={getCategory()}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full p-4 rounded-xl bg-dark-purple/60 border-2 border-white/30 text-white text-base focus:border-cyan focus:outline-none"
                    >
                      <option value="" className="bg-dark-purple text-white">Select category...</option>
                      <option value="Image" className="bg-dark-purple text-white">Image</option>
                      <option value="Video" className="bg-dark-purple text-white">Video</option>
                      <option value="Audio" className="bg-dark-purple text-white">Audio</option>
                      <option value="3D" className="bg-dark-purple text-white">3D</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-yellow font-bold text-base mb-3">
                      Description
                    </label>
                    <textarea
                      value={editedCosyflow.data.description || ''}
                      onChange={(e) => setEditedCosyflow({
                        ...editedCosyflow,
                        data: {
                          ...editedCosyflow.data,
                          description: e.target.value
                        }
                      })}
                      placeholder="Enter a description for your cosyflow..."
                      rows="3"
                      className="w-full p-4 rounded-xl bg-dark-purple/60 border-2 border-white/30 text-white text-base placeholder-white/40 focus:border-cyan focus:outline-none resize-y"
                    />
                  </div>

                  <hr className="border-white/20 my-4" />
                  <div className="mb-4">
                    <label className="block text-yellow font-bold text-base mb-3">
                      Trusted
                    </label>
                    <div className={`w-full p-4 rounded-xl border-2 text-base font-semibold ${
                      editedCosyflow.trusted == 1 || editedCosyflow.trusted === true
                        ? 'bg-green-500/20 border-green-500/50 text-green-300'
                        : 'bg-red-500/20 border-red-500/50 text-red-300'
                    }`}>
                      {editedCosyflow.trusted == 1 || editedCosyflow.trusted === true ? 'Trusted' : 'Not Trusted'}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-dark-purple/30 rounded-xl border border-white/20">
                    <div>
                      <span className="text-white text-sm font-medium">Visibility</span>
                      <p className="text-white/50 text-xs">
                        {editedCosyflow.public == 1 || editedCosyflow.public === true ? 'Public' : 'Private'}
                        {editedCosyflow.isCustom && !(editedCosyflow.trusted == 1 || editedCosyflow.trusted === true) && ' (Requires trust)'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setEditedCosyflow({
                          ...editedCosyflow,
                          public: editedCosyflow.public == 1 || editedCosyflow.public === true ? 0 : 1
                        });
                      }}
                      disabled={editedCosyflow.isCustom && !(editedCosyflow.trusted == 1 || editedCosyflow.trusted === true)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editedCosyflow.isCustom && !(editedCosyflow.trusted == 1 || editedCosyflow.trusted === true)
                          ? 'bg-white/20 cursor-not-allowed'
                          : editedCosyflow.public == 1 || editedCosyflow.public === true
                            ? 'bg-cyan'
                            : 'bg-white/30'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editedCosyflow.public == 1 || editedCosyflow.public === true ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-dark-purple/30 rounded-xl border border-white/20">
                    <div>
                      <span className="text-white text-sm font-medium">Distributed</span>
                      <p className="text-white/50 text-xs">
                        {editedCosyflow.distributed == 1 || editedCosyflow.distributed === true ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setEditedCosyflow({
                          ...editedCosyflow,
                          distributed: editedCosyflow.distributed == 1 || editedCosyflow.distributed === true ? 0 : 1
                        });
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editedCosyflow.distributed == 1 || editedCosyflow.distributed === true ? 'bg-cyan' : 'bg-white/30'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editedCosyflow.distributed == 1 || editedCosyflow.distributed === true ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3 pt-6 border-t-2 border-white/30">
                    <div className="flex items-center justify-between p-3 bg-dark-purple/30 rounded-xl border border-white/20">
                      <span className="text-white text-sm font-medium">Install Cosyflow locally</span>
                      <button
                        type="button"
                        onClick={handleInstallLocallyToggle}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          installLocally ? 'bg-cyan' : 'bg-white/30'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            installLocally ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <button
                      onClick={isFromComfyUI ? handleSubmit : handleUpdate}
                      disabled={saving || !editedCosyflow.data.title}
                      className="w-full bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-4 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base shadow-lg hover:shadow-xl"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-dark-blue"></div>
                          {isFromComfyUI ? 'Saving...' : 'Updating...'}
                        </>
                      ) : (
                        <>
                          <span>💾</span>
                          {isFromComfyUI ? 'Save as New Cosyflow' : 'Update Cosyflow'}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setEditedCosyflow(null);
                        setSelectedCosyflowId(null);
                        setExampleImages([]);
                        setMessage(null);
                      }}
                      disabled={saving}
                      className="w-full bg-dark-purple/60 hover:bg-dark-purple/80 text-white font-bold py-4 px-6 rounded-xl transition-all border-2 border-white/30 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-white/60 text-base">Select a cosyflow to edit properties</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Picker Modal */}
      <ModelGalleryPicker
        isOpen={isGalleryPickerOpen}
        onClose={() => setIsGalleryPickerOpen(false)}
        modelName={editedCosyflow?.data?.title}
        onSelect={handleGallerySelect}
        selectedMedia={exampleImages}
        filterByModel={false} 
        dataType={getCategory().toLowerCase()}
        limit={15}
      />
    </div>
  );
};

export default CosyflowEditor;